import React, { useEffect, useSyncExternalStore } from "react";
import DesignDialog, { DesignDialogButton } from "./DesignDialog";
import {
  getMissingFieldsPendingLeave,
  markMissingFieldsDialogOpen,
  subscribeMissingFieldsPendingLeave,
  takeMissingFieldsPendingLeave,
} from "./missingFieldsLeaveStore";

/**
 * The one dashboard REQUIRED FIELDS MISSING pop-up (CMP Dashboard (20)
 * CLAUDE.md "Required fields pop-up (global)"; Update Member Form.dc.html
 * errPop L1410-1432). Each row is one red box: "Section – Label" and the
 * reason. Closing it outlines and focuses the first failed field. The INVALID
 * EMAIL ADDRESS and PLEASE FIX THE FOLLOWING titles are the same pop-up
 * (Update Member Form umErrHead, Monday D17).
 */
export type MissingRequiredFieldsRow = {
  title: string;
  body: string;
  /** Card or section name, added before the title when there is more than one row. */
  section?: string;
  /** True for a filled field that is not valid (an email in the wrong format). */
  invalid?: boolean;
};

export type MissingRequiredFieldsVariant = "missing" | "invalidEmail" | "fixFollowing";

export const missingRequiredFieldsCopy: Record<MissingRequiredFieldsVariant, { title: string; message: string }> = {
  missing: { title: "REQUIRED FIELDS MISSING", message: "Fill out the following required fields and save again." },
  invalidEmail: { title: "INVALID EMAIL ADDRESS", message: "The email address entered is not valid. Correct it and save again." },
  fixFollowing: { title: "PLEASE FIX THE FOLLOWING", message: "Some fields are missing or not valid. Correct them and save again." },
};

/** Row text from the designs. */
export const missingRequiredFieldsText = {
  // Staff Create Member / Update Member Form (CMP Dashboard 19 L16587, UMF L2895).
  contactNameAndEmailTitle: "Contact Name & Contact Email",
  contactNameTitle: "Contact Name",
  contactEmailTitle: "Contact Email",
  accessNameAndEmail: "Name and email need to be filled to grant access.",
  accessName: "Name needs to be filled to grant access.",
  accessEmail: "A valid email needs to be filled to grant access.",
  // Member Dashboard Manage Company Profile (Monday D17).
  nameRequiredToGrantAccess: "Name is required to grant access.",
  emailRequiredToGrantAccess: "Email is required to grant access.",
  invalidEmail: "Invalid email address. Use the format name@company.com.",
  fieldRequired: "This field is required.",
} as const;

/** Every row invalid: INVALID EMAIL ADDRESS. Invalid and missing: PLEASE FIX THE FOLLOWING. */
export const getMissingRequiredFieldsVariant = (rows: readonly MissingRequiredFieldsRow[]): MissingRequiredFieldsVariant => {
  const invalid = rows.filter((row) => row.invalid).length;
  if (!invalid) return "missing";
  return invalid === rows.length ? "invalidEmail" : "fixFollowing";
};

/** Update Member Form.dc.html L2287: "Section – Label" when the section names more than the label. */
export const getMissingRequiredFieldsRowTitles = (rows: readonly MissingRequiredFieldsRow[]) =>
  rows.map((row) => row.section && row.section !== row.title ? `${row.section} \u2013 ${row.title}` : row.title);

const invalidFieldSelector = '[aria-invalid="true"], [data-member-field-error="true"]';
const focusableSelector = 'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])';
const highlightDurationMs = 4000;

const isShown = (element: HTMLElement) =>
  !element.closest('[role="dialog"]') && element.getClientRects().length > 0;

/**
 * Update Member Form.dc.html umApplyHl (L2250-2271): after the pop-up closes,
 * scroll to the first field that failed, focus it and outline it red. The
 * fields mark themselves with aria-invalid (or data-member-field-error), the
 * same state that shows their inline red message.
 */
export const focusFirstInvalidField = (root: ParentNode = document) => {
  const field = Array.from(root.querySelectorAll<HTMLElement>(invalidFieldSelector)).find(isShown);
  if (!field) return null;
  // A rich text editor focuses its text area, not its toolbar. A select box
  // and an editor outline their visible box (Oct 19 2026: Create Event Title).
  const focusTarget = field.matches(focusableSelector)
    ? field
    : field.querySelector<HTMLElement>('[contenteditable="true"]') || field.querySelector<HTMLElement>(focusableSelector) || field;
  const outlined = field.matches("input, textarea, select, [data-member-select-trigger], .rich-text-editor") ? field : focusTarget;
  field.scrollIntoView?.({ block: "center", behavior: "smooth" });
  focusTarget.focus({ preventScroll: true });
  const previous = { outline: outlined.style.outline, outlineOffset: outlined.style.outlineOffset, boxShadow: outlined.style.boxShadow };
  outlined.style.outline = "1px solid #E24C4C";
  outlined.style.outlineOffset = "0px";
  outlined.style.boxShadow = "0 0 0 3px rgba(226,76,76,.18)";
  outlined.dataset.requiredFieldHighlight = "true";
  const clear = () => {
    if (outlined.dataset.requiredFieldHighlight !== "true") return;
    delete outlined.dataset.requiredFieldHighlight;
    Object.assign(outlined.style, previous);
    outlined.removeEventListener("input", clear);
  };
  outlined.addEventListener("input", clear);
  window.setTimeout(clear, highlightDurationMs);
  return focusTarget;
};

/**
 * The access row for a contact whose dashboard access box is ticked
 * (CMP Dashboard 19 L16583-16588). Returns null when name and email are ready.
 */
export const getContactAccessMissingRow = ({ section, nameMissing, emailInvalid }: {
  section: string; nameMissing: boolean; emailInvalid: boolean;
}): MissingRequiredFieldsRow | null => {
  if (!nameMissing && !emailInvalid) return null;
  const text = missingRequiredFieldsText;
  return nameMissing && emailInvalid
    ? { section, title: text.contactNameAndEmailTitle, body: text.accessNameAndEmail }
    : nameMissing
      ? { section, title: text.contactNameTitle, body: text.accessName }
      : { section, title: text.contactEmailTitle, body: text.accessEmail };
};

export type MissingRequiredFieldsDialogProps = {
  open: boolean;
  rows: readonly MissingRequiredFieldsRow[];
  onClose: () => void;
  /** Defaults to the variant the rows need. */
  variant?: MissingRequiredFieldsVariant;
  /** Shows "Leave Without Saving" (also shown when the UNSAVED CHANGES save stopped here). */
  onLeaveWithoutSaving?: () => void;
};

const MissingRequiredFieldsDialog = ({ open, rows, onClose, variant, onLeaveWithoutSaving }: MissingRequiredFieldsDialogProps) => {
  const copy = missingRequiredFieldsCopy[variant || getMissingRequiredFieldsVariant(rows)];
  const titles = getMissingRequiredFieldsRowTitles(rows);
  const shown = open && rows.length > 0;
  const pendingLeave = useSyncExternalStore(subscribeMissingFieldsPendingLeave, getMissingFieldsPendingLeave);
  useEffect(() => (shown ? markMissingFieldsDialogOpen() : undefined), [shown]);
  const close = () => {
    takeMissingFieldsPendingLeave()?.stay();
    onClose();
    // After the pop-up and its focus trap are gone.
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => focusFirstInvalidField()));
  };
  const leaveWithoutSaving = onLeaveWithoutSaving || (pendingLeave ? () => takeMissingFieldsPendingLeave()?.leave() : undefined);
  return (
    <DesignDialog open={shown} onClose={close} variant="unsaved" ariaLabel={copy.title}
      // Update Member Form.dc.html errPop: width min(460px, 92vw), padding 30px 28px, border #ECECEC.
      panelStyle={{ padding: "30px 28px", borderColor: "#ECECEC", maxWidth: 460 }}>
      <div className="flex w-full flex-col items-center" data-testid="missing-required-fields-dialog">
        <div aria-hidden="true" className="flex h-[64px] w-[64px] shrink-0 items-center justify-center rounded-full bg-[#F21B1B] text-white">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="50%" height="50%">
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
        </div>
        <h2 className="mt-[18px] text-center text-[16px] font-extrabold leading-[normal] tracking-[.02em] text-[#0A0A0A]">{copy.title}</h2>
        <p className="mt-[8px] text-center text-[14px] font-semibold leading-[1.5] text-[#0A0A0A] [text-wrap:pretty]">{copy.message}</p>
        <ul className="m-0 mt-[20px] flex max-h-[min(50vh,420px)] w-full list-none flex-col gap-[10px] overflow-y-auto p-0 text-left">
          {rows.map((row, index) => (
            <li key={`${titles[index]}-${index}`} className="flex shrink-0 flex-col gap-[4px] rounded-[8px] bg-[#FDECEC] px-[16px] py-[12px]">
              <span className="text-[14px] font-bold leading-[normal] text-[#C81E1E]">{titles[index]}</span>
              <span className="text-[14px] leading-[1.5] text-[#C81E1E]">{row.body}</span>
            </li>
          ))}
        </ul>
        {leaveWithoutSaving && (
          <DesignDialogButton tone="dark" className="mt-[18px] w-full flex-none font-bold"
            // errPop button: 14px/700, radius 10px, padding 13px 20px.
            style={{ borderRadius: 10, padding: "13px 20px" }} onClick={leaveWithoutSaving}>
            Leave Without Saving
          </DesignDialogButton>
        )}
      </div>
    </DesignDialog>
  );
};

export default MissingRequiredFieldsDialog;
